import {
  APIFetchSurveyComponentsResponse,
  APIFieldAttributes,
  APISurveyAnswer,
  APISurveyAnswersResponse,
  APISurveyAttributes,
  APISurveyDeleteComponentResponse,
  APITagAttributes,
  ArdoqId,
  APIComponentHierarchy,
  UpdateType,
  APISurveyPendingApprovalsSummary,
  APISurveyChangeApprovalData,
  APISurveyApprovedChanges,
  UnpersistedEntity,
  APICurrentUser,
  ComponentOptionsResponse,
  AssetType,
  APICreateSurveyWithPermissionsResponse,
  APIResponseLogDataResponse,
  APINewComponentSurveyFormSubmission,
  ChangeDecision,
  APISurveyChangeApprovalRequestData,
  APIChangeApprovalShortCircuitData,
  ComponentSurveyApprovalStatus,
} from '@ardoq/api-types';
import {
  Method,
  crudApi,
  fetchJson,
  headers,
  renameEntity,
  toRequestOptions,
} from './api';
import { ApiResponse, SearchParams } from './types';
import { omit } from 'lodash';
import { ArdoqError, isArdoqError } from '@ardoq/common-helpers';
import { userApi } from './userApi';
import { workspaceFolderApi } from './workspaceFolderApi';

const createComponentFromAnswers = (
  surveyId: ArdoqId,
  broadcastInstanceId: ArdoqId | null,
  componentName: string,
  parentId: ArdoqId | null,
  answers: APISurveyAnswer[]
): ApiResponse<APISurveyAnswersResponse> => {
  const request = new Request(
    `/api/survey/${surveyId}/answers/${
      broadcastInstanceId ? `?broadcastInstanceId=${broadcastInstanceId}` : ''
    }`,
    toRequestOptions(Method.POST, {
      component: {
        name: componentName,
        parent: parentId ?? undefined,
      },
      answers,
    })
  );
  return fetchJson<APISurveyAnswersResponse>(request);
};

const deleteComponent = async (
  surveyId: ArdoqId,
  componentId: ArdoqId
): ApiResponse<APISurveyDeleteComponentResponse> => {
  const request = new Request(
    `/api/survey/${surveyId}/component/${componentId}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchJson<APISurveyDeleteComponentResponse>(request);
};

const fetchAnswers = (
  surveyId: ArdoqId,
  componentId: ArdoqId
): ApiResponse<APISurveyAnswersResponse> => {
  const request = new Request(
    `/api/survey/${surveyId}/answers/${componentId}`,
    { headers }
  );
  return fetchJson<APISurveyAnswersResponse>(request);
};

const fetchComponentHierarchy = (
  surveyId: ArdoqId
): ApiResponse<APIComponentHierarchy> => {
  const request = new Request(`/api/survey/${surveyId}/hierarchy`, { headers });
  return fetchJson<APIComponentHierarchy>(request);
};

const fetchComponents = (
  surveyId: ArdoqId,
  filter: string | null,
  broadcastInstanceId: string | null,
  searchParams: SearchParams
): ApiResponse<APIFetchSurveyComponentsResponse> => {
  const request = new Request(
    `/api/survey/${surveyId}/get-components`,
    toRequestOptions(Method.POST, {
      filter: filter ?? undefined,
      from: searchParams.from,
      size: searchParams.size,
      sort: searchParams.sortBy,
      order: searchParams.sortOrder,
      broadcastInstanceId: broadcastInstanceId ?? undefined,
    })
  );
  return fetchJson<APIFetchSurveyComponentsResponse>(request);
};

const fetchFields = (surveyId: ArdoqId): ApiResponse<APIFieldAttributes[]> => {
  const request = new Request(`/api/survey/${surveyId}/fields`, { headers });
  return fetchJson<APIFieldAttributes[]>(request);
};

const fetchComponentOptionsWithQuery = ({
  surveyId,
  questionId,
  componentId,
  query,
}: {
  surveyId: ArdoqId;
  questionId: ArdoqId;
  componentId?: ArdoqId;
  query: string;
}): ApiResponse<ComponentOptionsResponse> => {
  const request = new Request(
    `/api/survey/${surveyId}/question/${questionId}/options`,
    toRequestOptions(Method.POST, {
      componentId,
      q: query,
    })
  );
  return fetchJson<ComponentOptionsResponse>(request);
};

const fetchSurvey = (surveyId: ArdoqId): ApiResponse<APISurveyAttributes> => {
  const request = new Request(`/api/survey/${surveyId}`, { headers });
  return fetchJson<APISurveyAttributes>(request);
};

const fetchTags = (surveyId: ArdoqId): ApiResponse<APITagAttributes[]> => {
  const request = new Request(`/api/survey/${surveyId}/tags`, { headers });
  return fetchJson<APITagAttributes[]>(request);
};

const saveAnswers = (
  surveyId: ArdoqId,
  broadcastInstanceId: ArdoqId | null,
  componentId: ArdoqId,
  answers: APISurveyAnswer[]
): ApiResponse<APISurveyAnswersResponse> => {
  const request = new Request(
    `/api/survey/${surveyId}/answers/${componentId}${
      broadcastInstanceId
        ? `?broadcastInstanceId=${encodeURIComponent(broadcastInstanceId)}`
        : ''
    }`,
    toRequestOptions(Method.PUT, {
      answers,
      component: componentId,
    })
  );
  return fetchJson<APISurveyAnswersResponse>(request);
};

const postSurveyNotificationUnsubscription = (
  surveyId: ArdoqId,
  token: string,
  orgLabel: string
): ApiResponse<APISurveyAttributes> => {
  const request = new Request(
    `/api/survey/${surveyId}/digest/unsubscribe?org=${orgLabel}&token=${token}`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson<APISurveyAttributes>(request);
};

const fetchResponseLogData = (
  surveyId: ArdoqId,
  updateType: UpdateType
): ApiResponse<APIResponseLogDataResponse> => {
  const request = new Request(
    `/api/survey/${surveyId}/digest/history/${updateType}`,
    { headers }
  );
  return fetchJson<APIResponseLogDataResponse>(request).then(response => {
    if (isArdoqError(response)) return response;
    response.surveyId = surveyId;
    return response;
  });
};

const fetchPendingApprovalsSummary =
  (): ApiResponse<APISurveyPendingApprovalsSummary> => {
    const request = new Request('/api/survey/approval-summary', { headers });
    return fetchJson<APISurveyPendingApprovalsSummary>(request);
  };

const fetchPendingApproval = (
  surveyId: ArdoqId
): ApiResponse<APISurveyChangeApprovalData> => {
  const request = new Request(`/api/survey/${surveyId}/approval`, { headers });
  return fetchJson<APISurveyChangeApprovalData>(request);
};

const approvedChanges = (
  surveyId: ArdoqId,
  componentId: ArdoqId,
  approvedChanges: APISurveyApprovedChanges
): ApiResponse<APISurveyChangeApprovalData> => {
  const request = new Request(
    `/api/survey/${surveyId}/component/${componentId}/approval`,
    toRequestOptions(Method.POST, approvedChanges)
  );
  return fetchJson<APISurveyChangeApprovalData>(request);
};

const rejectChanges = (
  surveyId: ArdoqId,
  componentId: ArdoqId
): ApiResponse<APISurveyChangeApprovalData> => {
  const request = new Request(
    `/api/survey/${surveyId}/component/${componentId}/approval`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson<APISurveyChangeApprovalData>(request);
};

const resetAnswers = (
  surveyId: ArdoqId
): ApiResponse<APISurveyChangeApprovalData> => {
  const request = new Request(
    `/api/survey/${surveyId}/approval/reset`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson<APISurveyChangeApprovalData>(request);
};

const changeApprovalDecision = (
  changeRequestId: ArdoqId,
  decision: ChangeDecision,
  answers: APISurveyAnswer[]
): ApiResponse<Response> => {
  const request = new Request(
    `/api/change-approval/request/${changeRequestId}`,
    toRequestOptions(Method.POST, { decision, answers })
  );
  return fetch(request);
};

const resetInvalidChangeRequest = (
  surveyId: ArdoqId,
  componentId: ArdoqId
): ApiResponse<APISurveyChangeApprovalData> => {
  const request = new Request(
    `/api/survey/${surveyId}/component/${componentId}/reset`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson<APISurveyChangeApprovalData>(request);
};

const copy = (survey: APISurveyAttributes, newName: string) => {
  const freshSurvey = omit(
    {
      ...survey,
      name: newName,
      published: false,
      questions: survey.questions.map(question => omit(question, '_id')),
    },
    [
      '_id',
      '_version',
      'created',
      'createdBy',
      'createdByEmail',
      'createdByName',
      'lastModifiedBy',
      'lastUpdated',
      'lastModifiedByEmail',
      'lastModifiedByName',
      'ardoq',
      'ardoqPersistent',
      'meta',
      'rowType',
      'discoverPriority',
    ]
  ) as UnpersistedEntity<APISurveyAttributes>;
  return surveyApi.create(freshSurvey);
};

type FetchSimilarSurveyComponentsResponse = {
  total: number;
  components: { _id: ArdoqId; name: string }[];
};

const fetchSimilarSurveyComponents = (
  surveyId: ArdoqId,
  name: string,
  broadcastInstanceId?: ArdoqId
): ApiResponse<FetchSimilarSurveyComponentsResponse> => {
  const request = new Request(
    `/api/survey/${surveyId}/component-suggest`,
    toRequestOptions(Method.POST, { name, broadcastInstanceId })
  );
  return fetchJson<FetchSimilarSurveyComponentsResponse>(request);
};

const fetchSurveyFormData = async ({
  surveyId,
  componentId,
}: {
  surveyId: ArdoqId;
  componentId: ArdoqId | undefined;
}): Promise<
  | ArdoqError
  | {
      survey: APISurveyAttributes;
      fields: APIFieldAttributes[];
      currentUser: APICurrentUser;
      answersData: APISurveyAnswersResponse | null;
      shortCircuitOnCreate: boolean | null;
    }
> => {
  const [survey, fields, currentUser, answersData, shortCircuitResult] =
    await Promise.all([
      surveyApi.fetch(surveyId),
      fetchFields(surveyId),
      userApi.fetchCurrentUser(),
      componentId ? fetchAnswers(surveyId, componentId) : null,
      componentId ? null : surveyApi.fetchApprovalShortCircuit(surveyId),
    ]);

  if (isArdoqError(survey)) return survey;
  if (isArdoqError(fields)) return fields;
  if (isArdoqError(currentUser)) return currentUser;
  if (isArdoqError(answersData)) return answersData;
  if (isArdoqError(shortCircuitResult)) return shortCircuitResult;

  return {
    survey,
    fields,
    currentUser,
    answersData,
    shortCircuitOnCreate: shortCircuitResult
      ? shortCircuitResult.shortCircuitOnCreate
      : null,
  };
};

const createWithPermissions = (
  survey: UnpersistedEntity<APISurveyAttributes>
): ApiResponse<APICreateSurveyWithPermissionsResponse> => {
  const request = new Request(
    '/api/survey/create',
    toRequestOptions(Method.POST, survey)
  );
  return fetchJson<APICreateSurveyWithPermissionsResponse>(request);
};

const createInFolder = async (
  survey: UnpersistedEntity<APISurveyAttributes>,
  folderId: ArdoqId
): ApiResponse<APICreateSurveyWithPermissionsResponse> => {
  const response = await createWithPermissions(survey);
  if (isArdoqError(response)) {
    return response;
  }
  const moveAssetsResponse = await workspaceFolderApi.moveAssets({
    targetFolderId: folderId,
    content: [{ _id: response.survey._id, type: AssetType.SURVEY }],
  });
  if (isArdoqError(moveAssetsResponse)) {
    return moveAssetsResponse;
  }
  return response;
};

const submitSurveyForm = (
  surveyId: ArdoqId,
  body: APINewComponentSurveyFormSubmission,
  broadcastInstanceId?: ArdoqId
): ApiResponse<APISurveyAnswersResponse> => {
  const queryString = broadcastInstanceId
    ? `?broadcastInstanceId=${encodeURIComponent(broadcastInstanceId)}`
    : '';

  const request = new Request(
    `/api/survey/${surveyId}/answers/${queryString}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson<APISurveyAnswersResponse>(request);
};

const fetchComponentSurveys = (
  componentId: ArdoqId
): ApiResponse<APISurveyAttributes[]> => {
  const request = new Request(`/api/survey?componentId=${componentId}`, {
    headers,
  });
  return fetchJson<APISurveyAttributes[]>(request);
};

const fetchComponentSurveyApprovalStatuses = (
  componentId: ArdoqId
): ApiResponse<ComponentSurveyApprovalStatus[]> => {
  const request = new Request(`/api/survey/component/${componentId}`, {
    headers,
  });
  return fetchJson<ComponentSurveyApprovalStatus[]>(request);
};

const fetchChangeApprovalRequest = (changeRequestId: ArdoqId) => {
  const request = new Request(
    `/api/change-approval/request/${changeRequestId}`,
    { headers }
  );
  return fetchJson<APISurveyChangeApprovalRequestData>(request);
};

const fetchApprovalShortCircuit = (
  surveyId: ArdoqId
): ApiResponse<APIChangeApprovalShortCircuitData> => {
  const request = new Request(
    `/api/survey/${surveyId}/approval/short-circuit`,
    { headers }
  );
  return fetchJson<APIChangeApprovalShortCircuitData>(request);
};

export const surveyApi = {
  approvedChanges,
  rejectChanges,
  fetchPendingApproval,
  fetchPendingApprovalsSummary,
  fetchResponseLogData,
  postSurveyNotificationUnsubscription,
  createComponentFromAnswers,
  deleteComponent,
  fetchAnswers,
  fetchComponentHierarchy,
  fetchComponents,
  fetchFields,
  fetchComponentOptionsWithQuery,
  fetchSurvey,
  fetchTags,
  saveAnswers,
  resetAnswers,
  fetchChangeApprovalRequest,
  changeApprovalDecision,
  resetInvalidChangeRequest,
  rename: renameEntity('survey'),
  copy,
  fetchSimilarSurveyComponents,
  ...crudApi<APISurveyAttributes>('survey'),
  fetchSurveyFormData,
  createWithPermissions,
  createInFolder,
  submitSurveyForm,
  fetchComponentSurveys,
  fetchApprovalShortCircuit,
  fetchComponentSurveyApprovalStatuses,
};
